import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { Button } from './Button';
import { Sheet } from './Sheet';
import { cx } from './cx';
import styles from './UIProvider.module.css';

const UIContext = createContext(null);

const TOAST_ICONS = { success: CheckCircle2, error: XCircle, warning: AlertTriangle, info: Info };
const TOAST_DURATION = { success: 4000, info: 4000, warning: 6000, error: 7000 };

/**
 * Stellt Toasts (kurze Rückmeldungen) und Bestätigungsdialoge für die ganze App bereit.
 * Einmal um die App legen; in Komponenten useToast() bzw. useConfirm() verwenden.
 */
export function UIProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const nextId = useRef(1);

  const dismiss = useCallback((id) => setToasts((list) => list.filter((t) => t.id !== id)), []);

  const show = useCallback((message, { tone = 'info', title, duration } = {}) => {
    const id = nextId.current++;
    setToasts((list) => [...list.slice(-3), { id, tone, title, message }]);
    const ms = duration ?? TOAST_DURATION[tone] ?? 4000;
    if (ms > 0) setTimeout(() => dismiss(id), ms);
    return id;
  }, [dismiss]);

  const toast = useMemo(() => ({
    show,
    dismiss,
    success: (message, options) => show(message, { ...options, tone: 'success' }),
    error: (message, options) => show(message, { ...options, tone: 'error' }),
    warning: (message, options) => show(message, { ...options, tone: 'warning' }),
    info: (message, options) => show(message, { ...options, tone: 'info' })
  }), [show, dismiss]);

  const confirm = useCallback((options) => new Promise((resolve) => {
    setConfirmState({ ...options, resolve });
  }), []);

  const closeConfirm = (result) => {
    confirmState?.resolve(result);
    setConfirmState(null);
  };

  const value = useMemo(() => ({ toast, confirm }), [toast, confirm]);

  return (
    <UIContext.Provider value={value}>
      {children}

      <div className={styles.toasts} role="region" aria-label="Benachrichtigungen" aria-live="polite">
        {toasts.map(({ id, tone, title, message }) => {
          const Icon = TOAST_ICONS[tone] || Info;
          return (
            <div key={id} className={cx(styles.toast, styles[tone])} role={tone === 'error' ? 'alert' : 'status'}>
              <Icon className={styles.toastIcon} aria-hidden />
              <div className={styles.toastText}>
                {title && <strong>{title}</strong>}
                <span>{message}</span>
              </div>
              <button type="button" className={styles.toastClose} aria-label="Schließen" onClick={() => dismiss(id)}>
                <X aria-hidden />
              </button>
            </div>
          );
        })}
      </div>

      <Sheet
        open={!!confirmState}
        onClose={() => closeConfirm(false)}
        size="sm"
        title={confirmState?.title || 'Bist du sicher?'}
        footer={(
          <>
            <Button variant="secondary" onClick={() => closeConfirm(false)}>
              {confirmState?.cancelLabel || 'Abbrechen'}
            </Button>
            <Button variant={confirmState?.danger ? 'danger' : 'primary'} onClick={() => closeConfirm(true)} data-autofocus>
              {confirmState?.confirmLabel || 'Bestätigen'}
            </Button>
          </>
        )}
      >
        {confirmState?.message && <p className={styles.confirmText}>{confirmState.message}</p>}
      </Sheet>
    </UIContext.Provider>
  );
}

function useUI() {
  const context = useContext(UIContext);
  if (!context) throw new Error('UIProvider fehlt – bitte um die App legen');
  return context;
}

/** toast.success('Gespeichert'), toast.error('…'), toast.info(…), toast.warning(…) */
export const useToast = () => useUI().toast;

/** const ok = await confirm({ title, message, confirmLabel, danger: true }) */
export const useConfirm = () => useUI().confirm;
